import { useEffect, useMemo, useState } from "react";
import { Search, ChevronRight, LayoutList, FolderTree } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { useSortableData } from "@/lib/useSortableData";
import { PageHeader } from "@/components/common/PageHeader";
import { SortHeader } from "@/components/common/SortHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EntityDetailDialog } from "@/components/common/EntityDetailDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmt, fmtDecimal, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Loader } from "@/components/common/Loader";
import { apiFetch } from "@/lib/api";

const heat = (pct) => {
    if (pct >= 30) return "bg-success/12 text-success";
    if (pct >= 22) return "bg-warning/14 text-warning";
    if (pct > 0) return "bg-destructive/12 text-destructive";
    return "text-muted-foreground";
};

const flatten = (s) => ({
    id: s.samithi_id,
    name: s.samithi_name,
    district: s.district_name,
    category: s.category || "Samithi",
    members: s.membership.approved_members,
    youth_pct: s.membership.youth_share_pct,
    activities: s.full_year.reported_activities,
    attendance: s.full_year.total_attendance,
    avg: s.full_year.average_attendance_per_activity,
    yri_status: s.full_year.analytical_indices.youth_representation_index.status,
    raw: s,
});

export default function Samithis() {
    const { data, stateKey } = useDashboard();
    const [samithiData, setSamithiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [view, setView] = useState("grouped_by_district");
    const [query, setQuery] = useState("");
    const [district, setDistrict] = useState("all");
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);

    const stateSlug = stateKey || data?.entity?.slug || data?.entity?.state_slug || data?.state_slug;

    useEffect(() => {
        if (!stateSlug) {
            setLoadError("Unable to determine the selected State Organisation.");
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setLoadError(null);

        apiFetch(
            `/api/dashboard/states/${encodeURIComponent(stateSlug)}/samithis`,
            { signal: controller.signal },
        )
            .then((result) => {
                setSamithiData(result);
                setView(result?.default_view || "grouped_by_district");
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    setLoadError(error.message);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [stateSlug]);

    const explorer = useMemo(
        () =>
            samithiData || {
                default_view: "grouped_by_district",
                flat_table: [],
                grouped_by_district: [],
            },
        [samithiData],
    );

    const flatRows = useMemo(() => explorer.flat_table.map(flatten), [explorer]);
    const districtNames = useMemo(
        () => [...new Set(explorer.flat_table.map((s) => s.district_name))].sort(),
        [explorer],
    );

    const filtered = useMemo(
        () =>
            flatRows.filter(
                (r) =>
                    (district === "all" || r.district === district) &&
                    (r.name.toLowerCase().includes(query.toLowerCase()) ||
                        r.district.toLowerCase().includes(query.toLowerCase())),
            ),
        [flatRows, query, district],
    );
    const { sorted, sortKey, sortDir, toggle } = useSortableData(filtered, "activities", "desc");

    // grouped view with search applied
    const grouped = useMemo(() => {
        return explorer.grouped_by_district
            .map((g) => ({
                ...g,
                rows: g.samithis
                    .map(flatten)
                    .filter((r) => r.name.toLowerCase().includes(query.toLowerCase())),
            }))
            .filter((g) => g.rows.length > 0)
            .sort((a, b) => a.district_name.localeCompare(b.district_name));
    }, [explorer, query]);

    const openRow = (r) => {
        setSelected(r.raw);
        setOpen(true);
    };

    if (loading) return <Loader />;

    if (loadError) {
        return (
            <Card className="border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
                {loadError}
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                eyebrow="Samithis"
                title="Samithi explorer"
                description={`Browse all ${flatRows.length} samithis — grouped by district or as a single sortable table.`}
            >
                <Tabs value={view} onValueChange={setView}>
                    <TabsList>
                        <TabsTrigger value="grouped_by_district" className="gap-1.5 text-xs"><FolderTree className="h-3.5 w-3.5" /> Grouped</TabsTrigger>
                        <TabsTrigger value="flat_table" className="gap-1.5 text-xs"><LayoutList className="h-3.5 w-3.5" /> Table</TabsTrigger>
                    </TabsList>
                </Tabs>
            </PageHeader>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative w-full sm:max-w-xs">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search samithis…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" />
                </div>
                {view === "flat_table" && (
                    <Select value={district} onValueChange={setDistrict}>
                        <SelectTrigger className="w-full sm:w-56">
                            <SelectValue placeholder="All districts" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                            <SelectItem value="all">All districts</SelectItem>
                            {districtNames.map((d) => (
                                <SelectItem key={d} value={d}>{d}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <span className="text-sm text-muted-foreground sm:ml-auto">
                    {view === "flat_table" ? `${sorted.length} shown` : `${grouped.length} districts`}
                </span>
            </div>

            {view === "flat_table" ? (
                <Card className="overflow-hidden border-border/70 bg-card shadow-card">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border/70 hover:bg-transparent">
                                    <SortHeader label="Samithi" k="name" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} />
                                    <SortHeader label="District" k="district" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} />
                                    <SortHeader label="Category" k="category" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} />
                                    <SortHeader label="Members" k="members" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                    <SortHeader label="Youth %" k="youth_pct" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                    <SortHeader label="Activities" k="activities" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                    <SortHeader label="Attendance" k="attendance" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                    <SortHeader label="Avg" k="avg" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} align="right" />
                                    <TableCell className="w-8" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sorted.map((r) => (
                                    <TableRow key={r.id} onClick={() => openRow(r)} className="cursor-pointer border-border/50 transition-colors hover:bg-secondary/50">
                                        <TableCell className="font-medium text-foreground">{r.name}</TableCell>
                                        <TableCell className="text-muted-foreground">{r.district}</TableCell>
                                        <TableCell><StatusBadge tone="muted" label={r.category} dot={false} /></TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.members)}</TableCell>
                                        <TableCell className="text-right">
                                            <span className={cn("inline-block rounded-md px-2 py-0.5 text-sm font-medium tabular", heat(r.youth_pct))}>{fmtPct(r.youth_pct)}</span>
                                        </TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.activities)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.attendance)}</TableCell>
                                        <TableCell className="text-right tabular">{fmtDecimal(r.avg)}</TableCell>
                                        <TableCell className="text-right text-muted-foreground"><ChevronRight className="h-4 w-4" /></TableCell>
                                    </TableRow>
                                ))}
                                {sorted.length === 0 && (
                                    <TableRow><TableCell colSpan={9} className="py-10 text-center text-muted-foreground">No samithis match your filters</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            ) : (
                <Card className="border-border/70 bg-card p-2 shadow-card sm:p-4">
                    <Accordion type="multiple" className="w-full">
                        {grouped.map((g) => (
                            <AccordionItem key={g.district_id} value={String(g.district_id)} className="border-border/60">
                                <AccordionTrigger className="px-2 hover:no-underline">
                                    <span className="flex w-full items-center justify-between pr-3">
                                        <span className="font-medium text-foreground">{g.district_name}</span>
                                        <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{g.rows.length} samithis</span>
                                    </span>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableBody>
                                                {g.rows.map((r) => (
                                                    <TableRow key={r.id} onClick={() => openRow(r)} className="cursor-pointer border-border/40 transition-colors hover:bg-secondary/50">
                                                        <TableCell className="font-medium text-foreground">{r.name}<div className="mt-1"><StatusBadge tone="muted" label={r.category} dot={false} /></div></TableCell>
                                                        <TableCell className="text-right">
                                                            <span className={cn("inline-block rounded-md px-2 py-0.5 text-xs font-medium tabular", heat(r.youth_pct))}>{fmtPct(r.youth_pct)} youth</span>
                                                        </TableCell>
                                                        <TableCell className="text-right tabular text-sm">{fmt(r.activities)} acts</TableCell>
                                                        <TableCell className="text-right tabular text-sm">{fmt(r.attendance)} att.</TableCell>
                                                        <TableCell className="text-right tabular text-sm">Avg {fmtDecimal(r.avg)}</TableCell>
                                                        <TableCell className="text-right">
                                                            <StatusBadge status={r.yri_status} />
                                                        </TableCell>
                                                        <TableCell className="w-8 text-right text-muted-foreground"><ChevronRight className="h-4 w-4" /></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Card>
            )}

            <EntityDetailDialog entity={selected} type="samithi" open={open} onOpenChange={setOpen} />
        </div>
    );
}
